import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon, SparklesIcon, TrendingUpIcon } from 'lucide-react';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const isHomePage = location.pathname === '/';
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);
  // Trap focus in mobile menu when open
  useEffect(() => {
    if (isMobileMenuOpen && mobileMenuRef.current) {
      const focusableElements = mobileMenuRef.current.querySelectorAll('a[href], button:not([disabled])');
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
        if (e.key === 'Escape') {
          setIsMobileMenuOpen(false);
        }
      };
      document.addEventListener('keydown', handleTabKey);
      firstElement?.focus();
      return () => {
        document.removeEventListener('keydown', handleTabKey);
      };
    }
  }, [isMobileMenuOpen]);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setActiveDropdown(null);
    }
  };
  const toggleDropdown = (menu: string) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };
  const services = [{
    name: 'Web Design',
    path: '/services/web-design'
  }, {
    name: 'E-Commerce',
    path: '/services/ecommerce'
  }, {
    name: 'Branding',
    path: '/services/branding'
  }, {
    name: 'SEO Services',
    path: '/services/seo'
  }, {
    name: 'Website Maintenance',
    path: '/services/maintenance'
  }, {
    name: 'Content Writing',
    path: '/services/content'
  }];
  const policies = [{
    name: 'All Policies',
    path: '/policies'
  }, {
    name: 'Privacy & Cookies',
    path: '/privacy'
  }, {
    name: 'Cancellation Policy',
    path: '/cancellation'
  }, {
    name: 'Terms & Conditions',
    path: '/terms'
  }];
  // Determine header background color based on page and scroll state
  const getHeaderBgClass = () => {
    return 'bg-white dark:bg-[#0C1B26] shadow-md';
  };
  // Determine text color for links based on page and scroll state
  const getLinkColorClass = (isActive: boolean) => {
    if (isActive) return 'text-[#0DC3DC]';
    return 'text-gray-700 dark:text-gray-200 hover:text-[#0DC3DC]';
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${getHeaderBgClass()}`} data-id="element-705">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-706">
        <div className="flex items-center justify-between h-24 md:h-28" data-id="element-707">
          {/* Logo */}
          <Link to="/" className="flex flex-col items-start" data-id="element-708">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wvasxNr8E7D9LdV9uBEcfn/Header_Bright_Pixxel_Logo_.png" alt="Bright Pixxel Studios - UK Web Design" className="h-16 md:h-20" data-id="element-709" />
            <span className="text-[10px] md:text-xs text-gray-600 dark:text-gray-400 font-medium tracking-wide mt-1" data-id="element-710">
              Affordable, Creative, Digital Excellence
            </span>
          </Link>
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6" data-id="element-711">
            <Link to="/" className={`text-sm font-medium transition-colors ${getLinkColorClass(location.pathname === '/')}`} data-id="element-712">
              Home
            </Link>
            {/* Services Dropdown */}
            <div className="relative group" data-id="element-713">
              <button className={`flex items-center text-sm font-medium transition-colors text-gray-700 dark:text-gray-200 hover:text-[#0DC3DC]`} data-id="element-714">
                Services
                <ChevronDownIcon size={16} className="ml-1 transition-transform group-hover:rotate-180 duration-200" data-id="element-715" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#212C34] rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-100 dark:border-gray-700 overflow-hidden" data-id="element-716">
                <div className="py-2" data-id="element-717">
                  {services.map((service, index) => <Link key={service.path} to={service.path} className="block px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-[#0DC3DC]/10 hover:to-transparent hover:text-[#0DC3DC] transition-all duration-150" data-id="element-718">
                      {service.name}
                    </Link>)}
                </div>
              </div>
            </div>
            {/* Pricing - Highlighted */}
            <Link to="/pricing" className="relative group" data-id="element-719">
              <motion.div whileHover={{
              scale: 1.05
            }} className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${location.pathname === '/pricing' ? 'bg-gradient-to-r from-[#0DC3DC] to-[#00A8BF] text-white shadow-lg shadow-[#0DC3DC]/30' : 'bg-gradient-to-r from-[#0DC3DC]/10 to-[#00A8BF]/10 text-[#0DC3DC] hover:from-[#0DC3DC]/20 hover:to-[#00A8BF]/20 border border-[#0DC3DC]/30'}`} data-id="element-720">
                <SparklesIcon size={14} className="animate-pulse" data-id="element-721" />
                <span data-id="element-722">Pricing</span>
              </motion.div>
            </Link>
            {/* Portfolio - Highlighted */}
            <Link to="/portfolio" className="relative group" data-id="element-723">
              <motion.div whileHover={{
              scale: 1.05
            }} className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${location.pathname === '/portfolio' ? 'bg-gradient-to-r from-[#FF8A3D] to-[#FFA726] text-white shadow-lg shadow-[#FF8A3D]/30' : 'bg-gradient-to-r from-[#FF8A3D]/10 to-[#FFA726]/10 text-[#FF8A3D] hover:from-[#FF8A3D]/20 hover:to-[#FFA726]/20 border border-[#FF8A3D]/30'}`} data-id="element-724">
                <TrendingUpIcon size={14} data-id="element-725" />
                <span data-id="element-726">Portfolio</span>
              </motion.div>
            </Link>
            <Link to="/about" className={`text-sm font-medium transition-colors ${getLinkColorClass(location.pathname === '/about')}`} data-id="element-727">
              About Us
            </Link>
            <Link to="/contact" className={`text-sm font-medium transition-colors ${getLinkColorClass(location.pathname === '/contact')}`} data-id="element-728">
              Contact Us
            </Link>
            <Link to="/faq" className={`text-sm font-medium transition-colors ${getLinkColorClass(location.pathname === '/faq')}`} data-id="element-729">
              FAQ
            </Link>
            {/* Policies Dropdown */}
            <div className="relative group" data-id="element-730">
              <button className={`flex items-center text-sm font-medium transition-colors text-gray-700 dark:text-gray-200 hover:text-[#0DC3DC]`} data-id="element-731">
                Policies
                <ChevronDownIcon size={16} className="ml-1 transition-transform group-hover:rotate-180 duration-200" data-id="element-732" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#212C34] rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-100 dark:border-gray-700 overflow-hidden" data-id="element-733">
                <div className="py-2" data-id="element-734">
                  {policies.map(policy => <Link key={policy.path} to={policy.path} className="block px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-[#0DC3DC]/10 hover:to-transparent hover:text-[#0DC3DC] transition-all duration-150" data-id="element-735">
                      {policy.name}
                    </Link>)}
                </div>
              </div>
            </div>
            <Link to="/contact" className="px-6 py-2.5 bg-gradient-to-r from-[#0DC3DC] to-[#00A8BF] text-white font-medium rounded-lg hover:shadow-lg hover:shadow-[#0DC3DC]/30 transition-all duration-300" data-id="element-736">
              Get Started
            </Link>
          </div>
          {/* Mobile Menu Button */}
          <button onClick={toggleMobileMenu} className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMobileMenuOpen} data-id="element-737">
            {isMobileMenuOpen ? <XIcon size={24} className="text-gray-700 dark:text-white" data-id="element-738" /> : <MenuIcon size={24} className="text-gray-700 dark:text-white" data-id="element-739" />}
          </button>
        </div>
        {/* Mobile Menu */}
        <AnimatePresence data-id="element-740">
          {isMobileMenuOpen && <motion.div ref={mobileMenuRef} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="lg:hidden overflow-hidden bg-[#0C1B26] border-t border-gray-800" role="dialog" aria-modal="true" aria-label="Mobile navigation menu" data-id="element-741">
              <div className="py-4 space-y-2" data-id="element-742">
                <Link to="/" className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${location.pathname === '/' ? 'bg-[#0DC3DC]/10 text-[#0DC3DC]' : 'text-gray-200 hover:bg-gray-800'}`} data-id="element-743">
                  Home
                </Link>
                {/* Mobile Services Dropdown */}
                <div data-id="element-744">
                  <button onClick={() => toggleDropdown('services')} className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-gray-200 hover:bg-gray-800 rounded-lg transition-colors" data-id="element-745">
                    Services
                    <ChevronDownIcon size={20} className={`transition-transform ${activeDropdown === 'services' ? 'rotate-180' : ''}`} data-id="element-746" />
                  </button>
                  <AnimatePresence data-id="element-747">
                    {activeDropdown === 'services' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} className="pl-4 space-y-1 overflow-hidden" data-id="element-748">
                        {services.map(service => <Link key={service.path} to={service.path} className="block px-4 py-2 text-sm text-gray-300 hover:text-[#0DC3DC] transition-colors" data-id="element-749">
                            {service.name}
                          </Link>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>
                {/* Mobile Pricing - Highlighted */}
                <Link to="/pricing" className="block mx-4 px-4 py-3 text-base font-semibold rounded-lg transition-all bg-gradient-to-r from-[#0DC3DC] to-[#00A8BF] text-white shadow-lg" data-id="element-750">
                  <span className="flex items-center gap-2" data-id="element-751">
                    <SparklesIcon size={16} data-id="element-752" />
                    Pricing
                  </span>
                </Link>
                {/* Mobile Portfolio - Highlighted */}
                <Link to="/portfolio" className="block mx-4 px-4 py-3 text-base font-semibold rounded-lg transition-all bg-gradient-to-r from-[#FF8A3D] to-[#FFA726] text-white shadow-lg" data-id="element-753">
                  <span className="flex items-center gap-2" data-id="element-754">
                    <TrendingUpIcon size={16} data-id="element-755" />
                    Portfolio
                  </span>
                </Link>
                <Link to="/about" className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${location.pathname === '/about' ? 'bg-[#0DC3DC]/10 text-[#0DC3DC]' : 'text-gray-200 hover:bg-gray-800'}`} data-id="element-756">
                  About Us
                </Link>
                <Link to="/contact" className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${location.pathname === '/contact' ? 'bg-[#0DC3DC]/10 text-[#0DC3DC]' : 'text-gray-200 hover:bg-gray-800'}`} data-id="element-757">
                  Contact Us
                </Link>
                <Link to="/faq" className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${location.pathname === '/faq' ? 'bg-[#0DC3DC]/10 text-[#0DC3DC]' : 'text-gray-200 hover:bg-gray-800'}`} data-id="element-758">
                  FAQ
                </Link>
                {/* Mobile Policies Dropdown */}
                <div data-id="element-759">
                  <button onClick={() => toggleDropdown('policies')} className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-gray-200 hover:bg-gray-800 rounded-lg transition-colors" data-id="element-760">
                    Policies
                    <ChevronDownIcon size={20} className={`transition-transform ${activeDropdown === 'policies' ? 'rotate-180' : ''}`} data-id="element-761" />
                  </button>
                  <AnimatePresence data-id="element-762">
                    {activeDropdown === 'policies' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} className="pl-4 space-y-1 overflow-hidden" data-id="element-763">
                        {policies.map(policy => <Link key={policy.path} to={policy.path} className="block px-4 py-2 text-sm text-gray-300 hover:text-[#0DC3DC] transition-colors" data-id="element-764">
                            {policy.name}
                          </Link>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <Link to="/contact" className="block mx-4 mt-4 px-6 py-3 bg-[#0DC3DC] text-white font-medium text-center rounded-lg hover:bg-[#0DC3DC]/90 transition-colors" data-id="element-765">
                  Get Started
                </Link>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>
    </header>;
}